import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { ownAccountSchema } from "./account";

const text = (limit: number) =>
  z
    .string()
    .refine(
      (value) => Array.from(value).length <= limit,
      `Must contain at most ${limit} characters`,
    );
export const accountProfileUpdateSchema = z
  .strictObject({
    firstName: text(32).optional(),
    lastName: text(32).optional(),
    phone: text(32).optional(),
  })
  .refine(
    (value) => Object.values(value).some((value) => value !== undefined),
    "An edit is required",
  );
export const accountPreferencesUpdateSchema = z
  .strictObject({
    style: z.enum(["Bullet Point", "Paragraph"]).optional(),
    format: z.enum(["Detailed", "Concise"]).optional(),
    country: text(50).optional(),
    state: text(50).optional(),
    city: text(50).optional(),
    occupationId: z.int().positive().optional(),
    transcriptionLanguageId: z.int().positive().optional(),
    visitRetentionPeriodId: z.int().positive().optional(),
  })
  .refine(
    (value) => Object.values(value).some((value) => value !== undefined),
    "An edit is required",
  );
export type AccountProfileUpdate = z.infer<typeof accountProfileUpdateSchema>;
export type AccountPreferencesUpdate = z.infer<
  typeof accountPreferencesUpdateSchema
>;
const write = oc.errors({
  BAD_REQUEST: { message: "Invalid request" },
  METHOD_NOT_ALLOWED: { message: "Method Not Allowed" },
  INTERNAL_SERVER_ERROR: { message: "Internal Server Error" },
  UNAUTHORIZED: { message: "Authentication required" },
  FORBIDDEN: { message: "Account required" },
  SERVICE_UNAVAILABLE: { message: "Authentication unavailable" },
});
export const accountEmailSchema = z.strictObject({ email: z.email().max(255) });
export const accountWriteContract = {
  replaceMyEmail: write
    .errors({ CONFLICT: { message: "Identity changed" } })
    .input(
      z.object({
        query: z.strictObject({}).default({}),
        body: accountEmailSchema,
      }),
    )
    .meta(
      openapi({
        method: "PUT",
        path: "/api/v2/me/email",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(accountEmailSchema),
  updateMe: write
    .input(
      z.object({
        query: z.strictObject({}).default({}),
        body: accountProfileUpdateSchema,
      }),
    )
    .meta(
      openapi({
        method: "PATCH",
        path: "/api/v2/me",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(ownAccountSchema),
  updateMyPreferences: write
    .input(
      z.object({
        query: z.strictObject({}).default({}),
        body: accountPreferencesUpdateSchema,
      }),
    )
    .meta(
      openapi({
        method: "PATCH",
        path: "/api/v2/me/preferences",
        inputStructure: "detailed",
        spec: (spec) => ({ ...spec, security: [{ bearerAuth: [] }] }),
      }),
    )
    .output(ownAccountSchema),
};
